import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MilestonePhaseRequestDto } from 'src/api/FiretrolApiClient';
import { milestonePhaseApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

/** Phase writes target either a job (`jobId`) or a milestone template (`templateId`). */
export interface MilestonePhaseScope {
  jobId?: number;
  templateId?: string;
}

const scopeKey = (scope: MilestonePhaseScope) =>
  String(scope.jobId ?? 'default');

export function useCreateMilestonePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      request,
    }: {
      scope: MilestonePhaseScope;
      request: MilestonePhaseRequestDto;
    }) => milestonePhaseApiClient.createPhase(scopeKey(scope), request),
    meta: {
      successMessage: 'Phase successfully created!',
      suppressErrorToast: true,
    },
    onSettled: (_data, _err, { scope }) =>
      invalidateFor('milestonePhase.save', qc, scope),
  });
}

export function useUpdateMilestonePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      phaseId,
      request,
    }: {
      scope: MilestonePhaseScope;
      phaseId: string;
      request: MilestonePhaseRequestDto;
    }) =>
      milestonePhaseApiClient.updatePhase(scopeKey(scope), phaseId, request),
    meta: {
      successMessage: 'Phase successfully updated!',
      suppressErrorToast: true,
    },
    onSettled: (_data, _err, { scope }) =>
      invalidateFor('milestonePhase.save', qc, scope),
  });
}

export function useDeleteMilestonePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      phaseId,
    }: {
      scope: MilestonePhaseScope;
      phaseId: string;
    }) => milestonePhaseApiClient.deletePhase(phaseId),
    meta: { successMessage: 'Phase removed!' },
    onSettled: (_data, _err, { scope }) =>
      invalidateFor('milestonePhase.save', qc, scope),
  });
}
